// KoinDX visual tokens: the brand palette (greys, white and mint), mint reserved for actions and
// positive states, Archivo for all text with its width axis expanded for amounts
export const tokens = {
  ink: "#111111", // page ground
  deck: "#1a1a1a", // surfaces: header, cards, modals
  well: "#282828", // wells and rows inside a surface, hairlines
  raised: "#353535", // hover, pressed, stronger borders
  line: "#282828",
  text: "#ffffff",
  muted: "#848484",
  faint: "#4C4C4C",
  mint: "#00ffbb",
  mintSoft: "#33ffc8", // hover
  mintDeep: "#00b282", // pressed
  onMint: "#111111",
};

export const fontFamily = '"Archivo Variable", "Archivo", system-ui, -apple-system, "Segoe UI", sans-serif';

// type scale (base 15px, ratio ~1.25): 12.5 / 14 / 15 / 18 / 22 / 28 / 34
export const amountType = {
  fontFamily,
  fontStretch: "125%",
  fontWeight: 520,
  letterSpacing: "-0.01em",
  fontVariantNumeric: "tabular-nums",
};

export const themeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: "dark",
    primary: {
      light: tokens.mintSoft,
      main: tokens.mint,
      dark: tokens.mintDeep,
      contrastText: tokens.onMint,
    },
    secondary: {
      dark: tokens.raised,
      main: tokens.muted,
      light: tokens.text,
      contrastText: tokens.text,
    },
    success: { main: tokens.mint },
    background: {
      default: tokens.ink,
      paper: tokens.deck,
      light: tokens.well,
    },
    divider: tokens.line,
    // primary/secondary are the MUI defaults; main/grey1/grey2 are the names the components use
    text: {
      primary: tokens.text,
      secondary: tokens.muted,
      disabled: tokens.faint,
      main: tokens.text,
      grey1: tokens.faint,
      grey2: tokens.muted,
    },
    action: {
      hover: "rgba(0, 255, 187, 0.06)",
      selected: "rgba(0, 255, 187, 0.1)",
      focus: "rgba(0, 255, 187, 0.12)",
      disabled: tokens.faint,
      disabledBackground: tokens.raised,
    },
  },
  typography: {
    fontFamily,
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 350,
    fontWeightRegular: 430,
    fontWeightMedium: 560,
    fontWeightBold: 680,
    h1: { fontSize: "2.125rem", fontWeight: 680, fontStretch: "112%", lineHeight: 1.15, letterSpacing: "-0.02em" },
    h2: { fontSize: "1.75rem", fontWeight: 680, fontStretch: "112%", lineHeight: 1.2, letterSpacing: "-0.015em" },
    h3: { fontSize: "1.75rem", fontWeight: 650, fontStretch: "112%", lineHeight: 1.2, letterSpacing: "-0.01em" },
    h4: { fontSize: "1.375rem", fontWeight: 650, fontStretch: "112%", lineHeight: 1.25 },
    h5: { fontSize: "1.375rem", fontWeight: 650, fontStretch: "112%", lineHeight: 1.25, letterSpacing: "-0.01em" },
    h6: { fontSize: "1.125rem", fontWeight: 600, fontStretch: "106%", lineHeight: 1.3 },
    subtitle1: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.35 },
    subtitle2: { fontSize: "0.875rem", fontWeight: 560, lineHeight: 1.4 },
    body1: { fontSize: "0.9375rem", fontWeight: 430, lineHeight: 1.45 },
    body2: { fontSize: "0.875rem", fontWeight: 430, lineHeight: 1.45 },
    caption: { fontSize: "0.78rem", fontWeight: 430, lineHeight: 1.4, color: tokens.muted },
    overline: { fontSize: "0.78rem", fontWeight: 560, letterSpacing: "0.02em", textTransform: "none" },
    button: { fontSize: "0.9375rem", fontWeight: 600, textTransform: "none", letterSpacing: "0" },
  },
  shape: {
    // hierarchy: surfaces 16, wells and buttons 12, pills fully round
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          colorScheme: "dark",
          scrollbarWidth: "thin",
          scrollbarColor: `${tokens.line} transparent`,
        },
        body: {
          backgroundColor: tokens.ink,
          color: tokens.text,
          fontVariantNumeric: "tabular-nums",
          WebkitFontSmoothing: "antialiased",
        },
        "::selection": { backgroundColor: "rgba(0, 255, 187, 0.25)" },
        // keyboard focus on native links and buttons gets the same ring as the MUI buttons; containers that only
        // take focus from code (modals, menus) keep none
        ":focus-visible": { outline: `2px solid ${tokens.mint}`, outlineOffset: "2px" },
        "[tabindex='-1']:focus-visible": { outline: "none" },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    // keyboard focus is always visible (mouse clicks do not show it)
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: `2px solid ${tokens.mint}`,
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.mint,
          "&:focus-visible": {
            outline: `2px solid ${tokens.mint}`,
            outlineOffset: "2px",
            borderRadius: "4px",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          minHeight: 40,
          paddingInline: 16,
          transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: "0.875rem", borderRadius: 10 },
        sizeLarge: { minHeight: 52, fontSize: "1rem" },
        containedPrimary: {
          backgroundColor: tokens.mint,
          color: tokens.onMint,
          "&:hover": { backgroundColor: tokens.mintSoft },
          "&:active": { backgroundColor: tokens.mintDeep },
          "&.Mui-disabled": { backgroundColor: tokens.raised, color: tokens.muted },
        },
        outlined: {
          borderColor: tokens.raised,
          color: tokens.text,
          "&:hover": { borderColor: tokens.mint, backgroundColor: "rgba(0, 255, 187, 0.04)" },
        },
        outlinedPrimary: {
          borderColor: tokens.raised,
          color: tokens.mint,
        },
        text: {
          color: tokens.mint,
          "&:hover": { backgroundColor: "rgba(0, 255, 187, 0.06)" },
        },
      },
      variants: [
        {
          // Max / Half next to the balances
          props: { variant: "dark-grey-contained" },
          style: {
            minHeight: 24,
            height: 24,
            paddingBlock: 0,
            minWidth: 0,
            paddingInline: 8,
            borderRadius: 8,
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: tokens.mint,
            backgroundColor: "rgba(0, 255, 187, 0.08)",
            "&:hover": { backgroundColor: "rgba(0, 255, 187, 0.16)" },
          },
        },
        {
          props: { variant: "grey-contained" },
          style: {
            minHeight: 30,
            minWidth: 30,
            padding: 0,
            backgroundColor: tokens.well,
            color: tokens.text,
            "&:hover": { backgroundColor: tokens.raised },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: tokens.muted,
          "&:hover": { color: tokens.text, backgroundColor: "rgba(255, 255, 255, 0.06)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.deck,
          backgroundImage: "none",
          border: `1px solid ${tokens.line}`,
          borderRadius: 16,
          boxShadow: "none",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: { slotProps: { title: { variant: "h5" } } },
      styleOverrides: {
        root: { padding: "20px 20px 8px" },
        title: { textTransform: "none" },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "12px 20px 20px",
          "&:last-child": { paddingBottom: 20 },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          // every modal panel shares the same surface, edge and shadow
          "& > .MuiBox-root, & > .MuiPaper-root": {
            backgroundColor: tokens.deck,
            border: `1px solid ${tokens.raised}`,
            borderRadius: 16,
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)",
            outline: "none",
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          "&:not(.MuiBackdrop-invisible)": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: tokens.deck, borderLeft: `1px solid ${tokens.line}` },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: { zIndex: 1300 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.raised,
          color: tokens.text,
          border: `1px solid ${tokens.line}`,
          fontSize: "0.8125rem",
          fontWeight: 430,
          padding: "8px 10px",
          maxWidth: 280,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 560, borderRadius: 999 },
        outlined: {
          borderColor: tokens.line,
          "&:hover": { borderColor: tokens.mint },
        },
        sizeMedium: { height: 30 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, alignItems: "center" },
        outlinedWarning: { color: tokens.text },
        outlinedError: { color: tokens.text },
        outlinedInfo: { borderColor: tokens.line, color: tokens.text },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: tokens.well,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.line },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.muted },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.mint, borderWidth: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: tokens.muted, "&.Mui-focused": { color: tokens.mint } },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: tokens.line, opacity: 1 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 560,
          color: tokens.muted,
          borderColor: tokens.line,
          "&.Mui-selected": {
            color: tokens.onMint,
            backgroundColor: tokens.mint,
            "&:hover": { backgroundColor: tokens.mintSoft },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.line },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: "transparent" },
        bar: { backgroundColor: tokens.mint },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          "&:hover": { backgroundColor: tokens.well },
        },
      },
    },
    // MUI 5 spacing between list icons and text (wallet and token lists)
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 56,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: `1px solid ${tokens.line}`,
          "&:before": { display: "none" },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.deck,
          borderTop: `1px solid ${tokens.line}`,
          height: 64,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: tokens.muted,
          "&.Mui-selected": { color: tokens.mint },
        },
        label: {
          fontSize: "0.8125rem",
          fontWeight: 560,
          "&.Mui-selected": { fontSize: "0.8125rem" },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: tokens.raised, color: tokens.text },
      },
    },
  },
};

export default themeOptions;
